<template>
    <div
        :class="[
            'nav-link-group',
            customClass,
            {
                'is-active': isHovered || isCopied,
                'has-copy': copyable && hasCopyValue,
                'nav-link-group-sm': size === 'sm'
            }
        ]"
        @mouseenter="isHovered = true"
        @mouseleave="isHovered = false"
    >
        <a
            v-if="href && href !== '#'"
            :href="href"
            :target="target"
            rel="noopener noreferrer"
            :class="['nav-link-btn', sizeClass]"
            v-b-tooltip.hover="tooltipConfig"
            role="button"
        >
            <span v-if="resolvedTypeIcon" class="nav-link-icon" v-html="resolvedTypeIcon"></span>
            <i v-else-if="resolvedIconClass" :class="['nav-link-icon', resolvedIconClass]"></i>
            <span class="nav-link-text">{{ displayText }}</span>
        </a>
        <span
            v-else
            :class="['nav-link-btn', 'disabled', sizeClass]"
            v-b-tooltip.hover="tooltipConfig"
        >
            <span v-if="resolvedTypeIcon" class="nav-link-icon" v-html="resolvedTypeIcon"></span>
            <i v-else-if="resolvedIconClass" :class="['nav-link-icon', resolvedIconClass]"></i>
            <span class="nav-link-text">{{ displayText }}</span>
        </span>

        <div
            v-if="copyable && hasCopyValue"
            class="nav-link-copy-tray"
            :class="{ 'show-tray': isHovered || isCopied }"
        >
            <span class="nav-link-pipe" aria-hidden="true"></span>
            <button
                type="button"
                class="nav-link-copy-btn"
                :class="{ 'is-copied': isCopied }"
                @click.stop.prevent="copyToClipboard"
                v-b-tooltip.hover="copyTooltipConfig"
                :aria-label="copyTooltipText"
            >
                <i :class="isCopied ? 'bi bi-check-lg' : 'bi bi-clipboard'"></i>
            </button>
        </div>
    </div>
</template>

<script>
    export const DEFAULT_TYPE_ICONS = {
        'user story': '<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="#009ccc" viewBox="0 0 16 16"><path d="M0 2a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2V2z"/></svg>',
        'userstory': '<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="#009ccc" viewBox="0 0 16 16"><path d="M0 2a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2V2z"/></svg>',
        'story': '<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="#009ccc" viewBox="0 0 16 16"><path d="M0 2a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2V2z"/></svg>',
        'product backlog item': '<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="#009ccc" viewBox="0 0 16 16"><path d="M0 2a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2V2z"/></svg>',
        'pbi': '<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="#009ccc" viewBox="0 0 16 16"><path d="M0 2a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2V2z"/></svg>',
        'requirement': '<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="#009ccc" viewBox="0 0 16 16"><path d="M0 2a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2V2z"/></svg>',
        'bug': '<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="#cc293d" viewBox="0 0 16 16"><circle cx="8" cy="8" r="8"/></svg>',
        'defect': '<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="#cc293d" viewBox="0 0 16 16"><circle cx="8" cy="8" r="8"/></svg>',
        'task': '<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="#f2c811" viewBox="0 0 16 16"><path d="M2 2h12v12H2z"/></svg>',
        'issue': '<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="#f2c811" viewBox="0 0 16 16"><path d="M2 2h12v12H2z"/></svg>',
        'impediment': '<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="#f2c811" viewBox="0 0 16 16"><path d="M2 2h12v12H2z"/></svg>',
        'feature': '<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="#773b93" viewBox="0 0 16 16"><path d="M8 1l7 4v6l-7 4-7-4V5z"/></svg>',
        'epic': '<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="#e06a3b" viewBox="0 0 16 16"><path d="M2 2h12v12H2z"/></svg>'
    };

    export function resolveWorkItemTypeIcon(typeIcon, workItemType) {
        if (typeIcon && typeof typeIcon === 'string') {
            const trimmed = typeIcon.trim();
            if (trimmed.startsWith('<svg') || trimmed.startsWith('<?xml') || trimmed.startsWith('<span')) {
                return trimmed;
            }
            if (/^(https?:\/\/|\/|data:image\/)/i.test(trimmed)) {
                return `<img src="${trimmed}" alt="" class="nav-link-icon-img" />`;
            }
            const tokenKey = trimmed.toLowerCase();
            if (DEFAULT_TYPE_ICONS[tokenKey]) {
                return DEFAULT_TYPE_ICONS[tokenKey];
            }
        }

        if (workItemType && typeof workItemType === 'string') {
            const normalized = workItemType.trim().toLowerCase();
            if (DEFAULT_TYPE_ICONS[normalized]) {
                return DEFAULT_TYPE_ICONS[normalized];
            }
        }

        return '';
    }

    export default {
        name: 'NavLinkButton',
        props: {
            href: {
                type: String,
                default: ''
            },
            id: {
                type: [String, Number],
                default: ''
            },
            prefix: {
                type: String,
                default: ''
            },
            text: {
                type: String,
                default: ''
            },
            typeIcon: {
                type: String,
                default: ''
            },
            workItemType: {
                type: String,
                default: ''
            },
            icon: {
                type: String,
                default: ''
            },
            isPr: {
                type: Boolean,
                default: false
            },
            title: {
                type: String,
                default: ''
            },
            target: {
                type: String,
                default: '_blank'
            },
            size: {
                type: String,
                default: 'sm'
            },
            copyable: {
                type: Boolean,
                default: true
            },
            copyValue: {
                type: [String, Number],
                default: ''
            },
            copyTitle: {
                type: String,
                default: ''
            },
            customClass: {
                type: String,
                default: ''
            },
            placement: {
                type: String,
                default: ''
            },
            popoverPlacement: {
                type: String,
                default: 'top'
            }
        },
        data() {
            return {
                isHovered: false,
                isCopied: false
            };
        },
        computed: {
            displayText() {
                if (this.text) return this.text;
                if (this.id !== null && this.id !== undefined && this.id !== '') {
                    return `${this.prefix}${this.id}`;
                }
                return '';
            },
            sizeClass() {
                return this.size === 'sm' ? 'nav-link-btn-sm' : '';
            },
            computedTitle() {
                if (this.title) return this.title;
                if (this.isPrOrPrId) return 'Open PR in Azure DevOps';
                return 'Open in Azure DevOps';
            },
            isPrOrPrId() {
                return this.isPr || (typeof this.id === 'string' && this.id.startsWith('!'));
            },
            isWorkItemId() {
                if (this.isPrOrPrId) return false;
                if ((typeof this.id === 'string' && this.id.startsWith('#')) || this.prefix === '#') return true;
                if (this.workItemType || this.typeIcon) return true;
                if (this.id !== null && this.id !== undefined && this.id !== '') {
                    const clean = String(this.id).trim();
                    return /^\d+$/.test(clean) || clean.startsWith('#');
                }
                return false;
            },
            resolvedTypeIcon() {
                return resolveWorkItemTypeIcon(this.typeIcon, this.workItemType);
            },
            resolvedIconClass() {
                if (this.icon) return this.icon;
                if (this.isPrOrPrId) return 'icon-azure-pr text-primary';
                if (this.isWorkItemId) return 'bi bi-card-checklist text-info';
                if (!this.resolvedTypeIcon) return 'bi bi-box-arrow-up-right';
                return '';
            },
            hasCopyValue() {
                return Boolean(this.copyValue || this.id || this.text);
            },
            copyTooltipText() {
                if (this.isCopied) return 'Copied!';
                if (this.copyTitle) return this.copyTitle;
                return 'Copy ID to clipboard';
            },
            effectivePlacement() {
                return this.placement || this.popoverPlacement || 'top';
            },
            tooltipConfig() {
                return {
                    title: this.computedTitle,
                    placement: this.effectivePlacement,
                    triggers: 'hover',
                    container: 'body',
                    boundary: 'window',
                    interactive: false,
                    customClass: 'action-hover-pop modal-hover-pop nav-link-popover'
                };
            },
            copyTooltipConfig() {
                return {
                    title: this.copyTooltipText,
                    placement: this.effectivePlacement,
                    triggers: 'hover',
                    container: 'body',
                    boundary: 'window',
                    interactive: false,
                    customClass: 'action-hover-pop modal-hover-pop nav-link-popover'
                };
            }
        },
        methods: {
            copyToClipboard() {
                let val = this.copyValue;
                if (!val) {
                    if (this.id !== null && this.id !== undefined && this.id !== '') {
                        val = String(this.id).replace(/^[!#]/, '');
                    } else if (this.text) {
                        val = this.text;
                    }
                }
                if (val && navigator.clipboard && navigator.clipboard.writeText) {
                    navigator.clipboard.writeText(String(val)).then(() => {
                        this.isCopied = true;
                        this.$emit('copied', val);
                        setTimeout(() => {
                            this.isCopied = false;
                        }, 1800);
                    }).catch(err => {
                        console.error('Failed to copy to clipboard', err);
                    });
                }
            }
        }
    };
</script>

<style scoped>
    .nav-link-group {
        display: inline-flex;
        align-items: stretch;
        vertical-align: middle;
        position: relative;
        padding: 0 !important;
        border: none !important;
        overflow: visible;
        max-width: 100%;
        white-space: nowrap;
        z-index: 2;
        transition: z-index 0.15s ease;
    }

    .nav-link-group:hover,
    .nav-link-group.is-active {
        z-index: 100;
    }

    .nav-link-btn {
        display: inline-flex;
        align-items: center;
        justify-content: flex-start;
        font-family: inherit;
        font-weight: 500;
        text-decoration: none !important;
        background-color: var(--vt-btn-light-bg, #f8f9fa);
        color: var(--vt-btn-light-color, #212529) !important;
        border: 1px solid var(--vt-btn-light-border, #ced4da);
        border-radius: 4px;
        padding: 0.15rem 0.45rem;
        font-size: 0.8rem;
        line-height: 1.4;
        max-width: 100%;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        box-sizing: border-box;
        transition: background-color 0.15s ease, border-color 0.15s ease, color 0.15s ease, box-shadow 0.15s ease;
        cursor: pointer;
        user-select: none;
    }

    .nav-link-btn-sm {
        padding: 0.1rem 0.35rem;
        font-size: 0.75rem;
    }

    /* Active / Hover state on link button */
    .nav-link-group:hover .nav-link-btn,
    .nav-link-group.is-active .nav-link-btn {
        background-color: #212529 !important;
        border-color: #212529 !important;
        color: #ffffff !important;
        box-shadow: 0 3px 12px rgba(0, 0, 0, 0.25);
    }

    /* Seamlessly flatten right corners when copy tray is attached and active */
    .nav-link-group.has-copy:hover .nav-link-btn,
    .nav-link-group.has-copy.is-active .nav-link-btn {
        border-top-right-radius: 0 !important;
        border-bottom-right-radius: 0 !important;
        border-right-color: transparent !important;
    }

    .nav-link-btn.disabled {
        opacity: 0.65;
        cursor: default;
        pointer-events: none;
    }

    .nav-link-icon {
        display: inline-flex;
        align-items: center;
        margin-right: 0.28rem;
        line-height: 1;
        flex-shrink: 0;
    }

    .nav-link-icon ::v-deep svg,
    .nav-link-icon ::v-deep img,
    .nav-link-icon-img {
        width: 14px;
        height: 14px;
        vertical-align: middle;
        object-fit: contain;
    }

    .nav-link-text {
        letter-spacing: -0.01em;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }

    /* The overlay copy tray that completes the single unified pill */
    .nav-link-copy-tray {
        position: absolute;
        top: 0;
        bottom: 0;
        left: calc(100% - 1px);
        height: 100%;
        box-sizing: border-box;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        background-color: #212529;
        border: 1px solid #212529;
        border-left: none;
        border-top-right-radius: 4px;
        border-bottom-right-radius: 4px;
        border-top-left-radius: 0;
        border-bottom-left-radius: 0;
        padding: 0 3px 0 0;
        opacity: 0;
        visibility: hidden;
        pointer-events: none;
        z-index: 10;
        box-shadow: 3px 3px 10px rgba(0, 0, 0, 0.25);
        transition: opacity 0.15s ease, transform 0.15s ease;
        transform: scaleX(0.9);
        transform-origin: left center;
    }

    .nav-link-group:hover .nav-link-copy-tray,
    .nav-link-group.is-active .nav-link-copy-tray,
    .nav-link-copy-tray.show-tray {
        opacity: 1;
        visibility: visible;
        pointer-events: auto;
        transform: scaleX(1);
    }

    /* The Pipe Divider */
    .nav-link-pipe {
        display: inline-block;
        width: 1px;
        height: 60%;
        min-height: 12px;
        background-color: rgba(255, 255, 255, 0.35);
        flex-shrink: 0;
        margin: 0 3px 0 1px;
        border-radius: 1px;
    }

    /* The Copy Icon Button */
    .nav-link-copy-btn {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        background: transparent;
        border: none;
        outline: none;
        box-shadow: none;
        color: #e2e8f0;
        padding: 0 4px;
        margin: 0;
        height: 100%;
        font-size: 0.75rem;
        line-height: 1;
        cursor: pointer;
        border-radius: 0 3px 3px 0;
        transition: background-color 0.15s ease, color 0.15s ease;
    }

    .nav-link-copy-btn:hover {
        background-color: rgba(255, 255, 255, 0.18);
        color: #ffffff;
    }

    .nav-link-copy-btn.is-copied {
        color: #22c55e !important;
    }

    /* Dark Mode Support */
    [data-theme="dark"] .nav-link-btn {
        background-color: #222d3d;
        color: #e2e8f0 !important;
        border-color: #3e4c5e;
    }

    [data-theme="dark"] .nav-link-group:hover .nav-link-btn,
    [data-theme="dark"] .nav-link-group.is-active .nav-link-btn {
        background-color: #007bff !important;
        border-color: #007bff !important;
        color: #ffffff !important;
        box-shadow: 0 3px 12px rgba(0, 123, 255, 0.4);
    }

    [data-theme="dark"] .nav-link-group.has-copy:hover .nav-link-btn,
    [data-theme="dark"] .nav-link-group.has-copy.is-active .nav-link-btn {
        border-right-color: transparent !important;
    }

    [data-theme="dark"] .nav-link-copy-tray {
        background-color: #007bff !important;
        border-color: #007bff !important;
        border-left: none !important;
        box-shadow: 3px 3px 10px rgba(0, 123, 255, 0.4);
    }

    [data-theme="dark"] .nav-link-pipe {
        background-color: rgba(255, 255, 255, 0.45);
    }

    [data-theme="dark"] .nav-link-copy-btn {
        color: #ffffff;
    }

    [data-theme="dark"] .nav-link-copy-btn:hover {
        background-color: rgba(0, 0, 0, 0.2);
        color: #ffffff;
    }

    [data-theme="dark"] .nav-link-copy-btn.is-copied {
        color: #4ade80 !important;
    }
</style>
